<script lang="ts" setup>
import Container from '@/components/Container.vue';
</script>

<template>
  <Container
    :class="css.content"
    tag="main"
    width="narrow"
  >
    <NuxtPage />
  </Container>
</template>

<style lang="scss" module="css">
.content {
  margin-top: var(--space-xlarge);
  margin-bottom: var(--space-large);
  flex: 1;

  // Typography for markdown content (pure HTML elements)
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 1.5em 0 1.5rem 0;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  h1 {
    font-size: var(--font-size-heading-1);
    font-weight: 600;
    line-height: 115%;
    letter-spacing: -0.03em;
  }

  h2 {
    font-size: var(--font-size-heading-2);
    font-weight: 600;
    line-height: 120%;
    letter-spacing: -0.02em;
  }

  h3 {
    font-size: var(--font-size-heading-3);
    font-weight: 600;
    line-height: 120%;
    letter-spacing: -0.01em;
  }

  h4 {
    font-size: var(--font-size-heading-4);
    font-weight: 600;
    line-height: 130%;
    letter-spacing: -0.02em;
  }

  h5 {
    font-size: var(--font-size-heading-5);
    font-weight: 600;
    line-height: 140%;
    letter-spacing: -0.01em;
  }

  h6 {
    font-size: var(--font-size-heading-6);
    font-weight: 600;
    line-height: 140%;
    letter-spacing: -0.01em;
  }

  p,
  ul,
  ol,
  pre,
  blockquote,
  table {
    margin: 1em 0 1em 0;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul ul,
  ol ol {
    margin-top: 0;
  }

  a {
    text-decoration: underline;
    text-underline-offset: 0.125em;
  }

  hr {
    margin: 2em 0;
    border: none;
    height: var(--divider-width, 1px);
    padding: 0;
    background: linear-gradient(90deg, var(--color-patient-200, #ffc3c3), var(--color-doctor-200, #c8e4f9));

    &:first-of-type {
      margin: var(--space-medium) auto;
    }

    &.animated {
      background: linear-gradient(
        135deg,
        var(--color-patient-200, $color-patient-200) 0%,
        var(--color-doctor-200, $color-doctor-200) 25%,
        var(--color-patient-200, $color-patient-200) 50%,
        var(--color-doctor-200, $color-doctor-200) 75%,
        var(--color-patient-200, $color-patient-200) 100%
      );
      animation: gradient-swipe 10s linear infinite;
      background-size: 400% 100%;
    }
  }

  blockquote {
    font-style: italic;
    padding-left: 1.5em;
    margin-left: 0em;
    border-left: var(--border-width, 1px) solid transparent;
    border-image: linear-gradient(180deg, var(--color-patient-200, #ffc3c3) 0%, var(--color-doctor-200, #c8e4f9) 100%) 1
      100%;
  }

  b,
  strong {
    font-weight: 600;
  }

  i,
  em {
    font-style: italic;
  }

  // Flow spacing for adjacent elements
  > * + * {
    margin-block-start: clamp(0.75rem, 1.5vw, 1.25rem);
  }
}

// Gradient animation keyframes
@keyframes gradient-swipe {
  0% {
    background-position: 0% 0%;
  }
  100% {
    background-position: -200% 0%;
  }
}
</style>
